import { useCallback, useMemo, useState } from 'react'
import { Platform, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native'
import { useTheme } from '../../../theme/useTheme'
import { useAccountSwitcher } from '../../contexts/AccountSwitcherContext'
import { useAvatarContext } from '../../../avatars/contexts/AvatarContext'
import { OrgSwitcherSheet } from './OrgSwitcherSheet'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { FriendAvatar } from '../../../../components/friends/shared/FriendAvatar'
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types'

const CHIP_SIZE = 28

interface OrgContextChipProps {
  hitSlop?: number
  testID?: string
}

/**
 * Shows which account (identity) the user is currently signed in as.
 * Tapping opens the {@link OrgSwitcherSheet} account switcher.
 *
 * Hidden when only 1 account is stored — nothing to switch between.
 * 28×28 chip, zero extra header space for single-account users.
 */
export function OrgContextChip({ hitSlop = 8, testID }: OrgContextChipProps) {
  const theme = useTheme()
  const { accounts, activeUserId } = useAccountSwitcher()
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext()
  const { width } = useWindowDimensions()
  const [open, setOpen] = useState(false)

  const activeAccount = useMemo(
    () => accounts.find((a) => a.userId === activeUserId) ?? null,
    [accounts, activeUserId],
  )

  const handlePress = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  // Only show when there are 2+ accounts — 1-account users get zero visual footprint.
  // Hide on narrow screens (mobile native + mobile web) — hamburger OrgSwitcherMenuRow covers it.
  const isNarrow = Platform.OS !== 'web' || width < 768
  if (accounts.length < 2 || isNarrow) return null

  const label = activeAccount?.displayName || activeAccount?.email || '?'
  // currentAvatarId is live from AvatarContext (no storage delay for the active user)
  const avatarId = (currentAvatarId ?? activeAccount?.zportiAvatarId ?? null) as DisplayAvatarId | null

  return (
    <>
      <Pressable
        onPress={handlePress}
        hitSlop={hitSlop}
        accessibilityRole="button"
        accessibilityLabel={`Active account: ${label}. Tap to switch.`}
        testID={testID ?? 'account-switcher-chip'}
        style={({ pressed, hovered }) => [
          styles.chip,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.chipPressed,
          hovered && Platform.OS === 'web' && { opacity: 0.85 },
        ]}
      >
        <FriendAvatar
          avatarId={avatarId}
          avatarConfig={currentAvatarConfig}
          fallbackUrl={activeAccount?.avatarUrl ?? undefined}
          size={CHIP_SIZE}
          nameForInitials={activeAccount?.displayName ?? activeAccount?.email ?? label}
          avatarRounded
          small
        />
        {/* Small indicator dot showing there are multiple accounts */}
        <View
          style={[
            styles.multiDot,
            { backgroundColor: theme.accent, borderColor: theme.background.primary },
          ]}
        />
      </Pressable>
      <OrgSwitcherSheet visible={open} onClose={handleClose} />
    </>
  )
}

const styles = StyleSheet.create({
  chip: {
    width: CHIP_SIZE,
    height: CHIP_SIZE,
    borderRadius: CHIP_SIZE / 2,
    overflow: 'visible',
  },
  chipPressed: {
    opacity: 0.85,
    transform: [{ scale: 0.96 }],
  },
  multiDot: {
    position: 'absolute',
    bottom: -1,
    right: -1,
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 1.5,
  },
})
